import React, { useState, useEffect, useLayoutEffect } from "react"
import { Modal } from "@mui/material"
import { useSelector, connect } from "react-redux"
import makeStyles from "@mui/styles/makeStyles"
import clsx from "clsx"

import InventoryHeader from "../../components/Reception/Inventory/InventoryHeader"
import GlobalStyles from "../../components/Reception/GlobalStyle"
import LeftSideWrapper from "../../components/Reception/LeftSideWrapper"
import RightSideWrapper from "../../components/Reception/RightSideWrapper"
import FullScreenWrapper from "../../components/FullScreenWrapper"
import Filter from "../../components/Reception/Filter"
import GroupItems from "../../components/Reception/GroupItems"
import ButtonFlaw from "../../components/Reception/ButtonFlaw"
import ActionContent from "../../components/Reception/ActionContent"
import ModalWrapper from "../../components/Reception/ModalWrapper"
import ActionHeaderText from "../../components/Reception/ActionHeaderText"
import ReceptionSnackBar from "../../components/Reception/ReceptionSnackBar"
import { checkTablet } from "../../utils"
import { TRANSFER_REFUSALS, hasAnyStockToBreak } from "../../utils/stockTypes"
import {
	closeInventoryDetailSnackBar,
	showInventoryInterface,
	updateProductStock,
	updateProductStockInventoryByFridge,
	transferProductStock,
	updateStatus,
	filterByStatus,
	filterByNameOrCode,
	filterByFridge,
	resetStatus,
	showErrors,
	loadInventory,
	sendProductPlacement
} from "../../actions/Reception/reception"
import { CAN_UPDATE_PRODUCT_STOCK } from "../../utils/accessRights"
import { findProductStock, getProductStock } from "../../utils/receptionUtils"
import { sortProductTypeKeys } from "../../utils/inventoryUtils"
import ActionFlawContent from "../../components/Reception/ActionFlawContent"
import { setSearchValue } from "../../utils"
import RoundedAddButton from "../../components/RoundedAddButton"
import InventoryAddModal from "./InventoryAddModal"

import { getProductTypeOptionsSelector, getProductTypesSelector } from "../../reducers/ProductTypes/productTypes"

const useStyles = makeStyles({
	filter: {
		// The page scrolls as well as the list: sticky keeps the search and fridge pills under the header either way.
		position: "sticky",
		top: 64,
		zIndex: 2,
		marginTop: 64,
		width: "57%",
		flexWrap: "wrap",
		// On tablet the list spans the full width and would show beside the sticky row.
		"&.is-tablet": {
			width: "100%"
		}
	}
})

const Inventory = (props) => {
	const {
		selectedHub,
		formattedInventoryData,
		update,
		showInventoryInterface,
		resetStatus,
		filterByStatus,
		filterByNameOrCode,
		updateStatus,
		updateProductStock,
		updateProductStockInventoryByFridge,
		transferProductStock,
		inventorySnackBar,
		closeInventoryDetailSnackBar,
		originalInventoryData,
		showErrors,
		canEdit,
		noDisplay,
		loadInventory,
		sendProductPlacement,
		filterByFridge,
		fridgeFilter
	} = props

	const classes = useStyles()

	// constants
	const hub = (selectedHub) ? selectedHub[0].name : ""
	// KFC-352: everything this ticket adds to the inventory screen — the fridge filter and the
	// two-stock tile — exists only for hubs with an active retail offer. Read once here so the
	// filter and the tiles can never disagree about which screen the user is on.
	const hasRetailOffer = Boolean(selectedHub && selectedHub[0] && selectedHub[0].activeRetailOffer)

	const subcontractorsProducts = useSelector(state => state.subcontractorsProducts.subcontractorsProductsList)
	const productTypeOptions = useSelector(getProductTypeOptionsSelector)
	// KFC-352: the full referential, unlike productTypeOptions which is filtered on
	// managementMode and misses every "manual" category.
	const productTypes = useSelector(getProductTypesSelector)

	// selected card state
	const [selectedCard, setSelectedCard] = useState([])
	const [open, setOpen] = useState(false)
	const [step, setStep] = useState(0)
	const [stepFlaw, setStepFlaw] = useState(0)
	const [status, setStatus] = useState("ALL")
	const [firstInput, setFirstInput] = useState()
	const [confirmInput, setConfirmInput] = useState()
	const [inventoryData, setInventoryData] = useState(formattedInventoryData)
	const [lostInput, setLostInput] = useState("")
	const [lostReason, setLostReason] = useState()
	const [lostVolume, setLostVolume] = useState()
	const [flawProduct, setFlawProduct] = useState()
	const [flawOrigin, setFlawOrigin] = useState(null)
	const [flawDlc, setFlawDlc] = useState("")
	const [selectedFlaw, setSelectedFlaw] = useState([])
	const [focusSearch, setFocusSearch] = useState(true)
	const [searchValue, updateSearchValue] = useState("")
	const [openModal, setOpenModal] = useState(false)

	// for device connected
	const [isTablet, setIsTablet] = useState(checkTablet)

	// effect on inventoryData change
	// type array of objects
	useEffect(() => {
		setInventoryData(formattedInventoryData)
	}, [update])

	// back to filter if no hub selected
	useEffect(() => {
		if (!selectedHub) {
			showInventoryInterface()
		}

		const storage = JSON.parse(localStorage.getItem("searchValue"))
		if (storage && storage.inventory) {
			updateSearchValue(storage.inventory)
			filterByNameOrCode(storage.inventory, true)
		}
	}, [])

	// focus search input
	useEffect(() => {
		if (step === 0) setFocusSearch(true)
		if (step !== 0) setFocusSearch(false)
	}, [step])

	// set isTablet state
	useLayoutEffect(() => {
		const checkisTablet = () => {
			setIsTablet(checkTablet())
		}

		document.title = "Inventaire - KFC"

		// check size
		window.addEventListener("resize", checkisTablet)

		// remove check
		return () => {
			window.removeEventListener("resize", checkisTablet)
			document.title = "KFC"
		}
	}, [])

	// open modal
	const handleClickOpenModal = () => {
		setOpen(true)
	}

	// close modal
	const handleCloseModal = async () => {
		if (stepFlaw > 0) {
			setStepFlaw(0)
			setFlawOrigin(null)
			resetLost()

			if (open) setOpen(false)
			return
		}

		setStep((step === 0) ? 0 : step - 1)

		// close modal then undo selection
		if (step === 1) {
			await resetStatus(selectedCard, true)
			resetSelection()
		}
	}

	const resetSelection = () => {
		setSelectedCard([])

		if (open) {
			setOpen(false)
		}
	}

	// on filter by status
	const onStatusChange = (selectedStatus) => {
		setStatus(selectedStatus)
		filterByStatus(selectedStatus, true)
	}

	// on filter by status
	// The field is uncontrolled, so this state is the only copy of the search that survives the
	// field being unmounted — which is what the flaw declaration does.
	const onNameChange = (nameOrCode) => {
		updateSearchValue(nameOrCode)
		filterByNameOrCode(nameOrCode, true)
		setSearchValue(nameOrCode, "inventory")
	}

	// on set flaw
	const onSetFlaw = () => {
		if (canEdit) {
			setStepFlaw(1)

			// open action in modal
			if (isTablet) {
				handleClickOpenModal()
			}
		}
	}

	// action on page quit
	const onClose = () => {
		filterByNameOrCode("", true)
		setSearchValue("", "inventory")
		showInventoryInterface()
	}

	const handleCardClick = async (element) => {
		if (selectedCard.length !== 0) await resetStatus(selectedCard, true)
		await updateStatus(element, true)

		setSelectedCard(element)
		setStep(1)

		// open action in modal
		if (isTablet) {
			handleClickOpenModal()
		}
	}

	const directValidation = async (input) => {
		await updateProductStock({ productStock: selectedCard, volume: input, type: "INVENTORY" })
		resetSelection()
		setStep(0)
	}

	/**
	 * KFC-285: records the inventory entered in the two-fridge panel.
	 *
	 * Deliberately not routed through `closeActionPanel`: that one resets `inventoryStatus` to
	 * `TODO`, which would undo the status the write just set. The action already frees the lock
	 * and refreshes the tile, so all that is left here is to drop the selection — exactly what
	 * `directValidation` does on the single-fridge screen.
	 */
	const confirmInventoryByFridge = async (counts) => {
		const saved = await updateProductStockInventoryByFridge({ productStock: selectedCard, counts })

		// On failure the panel stays open with the entry, and shows its own retry.
		if (!saved) return false

		resetSelection()
		setStep(0)

		return true
	}

	/**
	 * KFC-351 : le panneau demande le transfert, l'action decide, le panneau traduit le refus.
	 * On ne referme l'ecran que si le stock a reellement bouge — un refus doit laisser la saisie
	 * en place pour que le comptoir manager puisse corriger sa quantite.
	 */
	const confirmTransfer = async ({ from, to, quantity }) => {
		const outcome = await transferProductStock({ productStock: selectedCard, from, to, quantity })

		if (TRANSFER_REFUSALS.NONE !== outcome) return outcome

		resetSelection()
		setStep(0)

		return outcome
	}

	const handleValidate = async (e) => {
		e.preventDefault()

		if (step === 1) {
			if (selectedCard.events.length > 0) {
				const receptionElement = selectedCard.events.filter(elm => elm.type === "RECEPTION")
				if (receptionElement[0] && receptionElement[0].data.volume < parseInt(firstInput)) {
					setStep(2)
					return
				}
			}

			directValidation(firstInput)
			return
		}

		if (step === 2) {
			directValidation(confirmInput)
			return
		}
	}

	/**
	 * Adding a product that already has a stock: its click & collect fridge is declared at zero
	 * so the tile shows up and can then be counted.
	 *
	 * KFC-285: routed through the per-fridge action rather than `updateProductStock`, which sets
	 * `inventoryStatus` to `DONE` unconditionally. On a hub with a retail offer that would call
	 * the product inventoried while its self-service fridge had never been counted — a second,
	 * contradictory definition of "inventoried" living beside the one this ticket introduces.
	 * On a hub without a retail offer the outcome is unchanged: one declared fridge, counted.
	 */
	const handleUpdatePreexistingInventoryItem = async (code, dlc) => {
		const elem = getProductStock(originalInventoryData, code, dlc)
		if (!elem.volume) {
			await updateProductStockInventoryByFridge({ productStock: elem, counts: { clickAndCollect: 0 } })
		}
	}

	const handleFlawValidate = async (e) => {
		e.preventDefault()

		let products

		switch (stepFlaw) {
			case 1:
				products = findProductStock(originalInventoryData, flawProduct)
				setSelectedFlaw(products)

				if (products.length === 0) showErrors("Ce produit n'est pas sensé être en vente", true)
				if (products.length === 1) {
					if (products[0].receptionStatus !== "DONE") {
						showErrors("Ce produit n'est pas encore réceptionné", true)
						return
					}
					if (!hasAnyStockToBreak(products[0])) {
						showErrors("Le stock est vide sur ce produit, impossible de déclarer de la casse", true)
						return
					}
					setStepFlaw(3)
				}
				if (products.length > 1) setStepFlaw(2)

				return
			case 2:
				products = findProductStock(originalInventoryData, flawProduct, flawDlc)
				setSelectedFlaw(products)

				if (products.length === 0) showErrors("Aucun Produit trouvé", true)
				if (products.length > 0) {
					if (products[0].receptionStatus !== "DONE") {
						showErrors("Ce produit n'est pas encore réceptionné", true)
						return
					}
					if (!hasAnyStockToBreak(products[0])) {
						showErrors("Le stock est vide sur ce produit, impossible de déclarer de la casse", true)
						return
					}

					setStepFlaw(3)
				}

				return
			case 3:
				if (lostReason === "") {
					showErrors("Veuillez sélectionner un motif")
					return
				}

				const lost = {
					"volume": lostVolume,
					"reason": lostReason,
					"explanation": lostInput,
					"isInventory": true,
					"stockType": flawOrigin || undefined
				}
				await updateProductStock({ productStock: selectedFlaw[0], volume: lostVolume, lost, type: "LOSS" })
				setStepFlaw(0)
				setFlawOrigin(null)
				resetLost()

				return
			default:
				setStepFlaw(stepFlaw + 1)
		}
	}

	const inputActionChange = (value) => {
		if (step === 1) setFirstInput(value)
		if (step === 2) setConfirmInput(value)
	}

	const inputFlawActionChange = (value) => {
		if (stepFlaw === 1) setFlawProduct(value)
		if (stepFlaw === 2) setFlawDlc(value)
		if (stepFlaw === 3) setLostInput(value)
	}

	const resetLost = () => {
		setLostVolume("")
		setLostInput("")
		setLostReason("")
		setFlawDlc("")
		setFlawProduct("")
	}

	const refresh = () => {
		const storage = JSON.parse(localStorage.getItem("inventoryFilter"))
		if (storage) loadInventory(storage.brand, storage.hub)
	}

	const getAction = () => {
		if (stepFlaw > 0) {
			return (
				<ActionFlawContent
					stepFlaw={stepFlaw}
					handleClose={handleCloseModal}
					handleValidate={handleFlawValidate}
					setValue={inputFlawActionChange}
					isTablet={isTablet}
					flawProduct={flawProduct}
					flawDlc={flawDlc}
					lostActionInfos={[lostVolume, setLostReason, setLostVolume]}
					productStock={selectedFlaw && selectedFlaw[0]}
					hasTwoStocks={hasRetailOffer}
					onOriginChange={setFlawOrigin}
				/>
			)
		}

		return (
			<ActionContent
				step={step}
				lostActionInfos={[]}
				textHeader={(
					<ActionHeaderText
						selectedCard={selectedCard}
						productTypeOptions={productTypeOptions}
						productTypes={productTypes}
					/>
				)}
				selectedCard={selectedCard}
				openModal={() => { }}
				handleClose={handleCloseModal}
				handleValidate={handleValidate}
				setValue={inputActionChange}
				isTablet={isTablet}
				isInventory={true}
				hub={selectedHub && selectedHub[0]}
				fridgeFilter={fridgeFilter}
				onConfirmInventory={confirmInventoryByFridge}
				onTransfer={confirmTransfer}
				noDisplay={noDisplay}
			/>
		)
	}

	// KFC-352: Entrée / Plat / Dessert first, then the ProductTypes referential order.
	const listKeys = sortProductTypeKeys(Object.keys(inventoryData), productTypes)

	const onSendProductPlacement = () => {
		sendProductPlacement(selectedHub)
	}

	return (
		<FullScreenWrapper>
			{/*
			  * Declaring a flaw takes over the page, so the page chrome goes with it: the header
			  * and its way back out, and the filter row, which has no product list left to act on.
			  * The row also carries the clearance under the fixed header, so the two leave together
			  * or the form is pushed down by a header that is no longer there.
			  */}
			{stepFlaw === 0 &&
				<>
					{/* page header */}
					<InventoryHeader hub={hub} onClose={onClose} />
					<div className={clsx(classes.filter, { "is-tablet": isTablet })}>
						{/* filter upon device */}
						<Filter
							onStatusChange={onStatusChange}
							onNameChange={onNameChange}
							onSetFlaw={onSetFlaw}
							status={status}
							isTablet={isTablet}
							isInventory={true}
							refresh={refresh}
							focusSearch={focusSearch}
							searchValue={searchValue}
							sendProductPlacement={onSendProductPlacement}
							fridgeFilter={fridgeFilter}
							onFridgeFilterChange={filterByFridge}
							hasRetailOffer={hasRetailOffer}
						/>
					</div>
				</>
			}

			<div>
				{
					stepFlaw === 0 &&
					<>
						{/* left side section */}
						<LeftSideWrapper fullWidth={isTablet}>
							<>
								{listKeys && listKeys.map((elm, i) => {
									return (
										<GroupItems
											key={elm}
											canEdit={canEdit}
											name={elm}
											label={(productTypes.find(productType => productType.kfcName === elm) || {}).label}
											list={inventoryData[elm]}
											last={(i === listKeys.length - 1)}
											isTablet={isTablet}
											onCardClick={handleCardClick}
											selectedCard={selectedCard}
											isInventory={true}
											fridgeFilter={fridgeFilter}
											hasRetailOffer={hasRetailOffer}
										/>
									)
								})
								}

								{/* Button for flaw (break) */}
								<ButtonFlaw isTablet={true} onSetFlaw={onSetFlaw} />
							</>
						</LeftSideWrapper>

						{/* Right side section */}
						{!isTablet &&
							<RightSideWrapper>
								{getAction()}
							</RightSideWrapper>
						}
					</>
				}

				{
					stepFlaw !== 0 && !isTablet && getAction()
				}

				{/*
				  * Modal for input. `isTablet` is recomputed on every resize, but `open` is only
				  * set when the declaration starts. Starting one on a desktop width and then
				  * narrowing the window left the form rendered nowhere: too narrow for the
				  * desktop branch, and the modal still closed. With the page chrome gone there
				  * was nothing left to click either. The modal follows the step, not the order
				  * of the two switches.
				  */}
				{isTablet &&
					<ModalWrapper
						open={open || stepFlaw > 0}
						handleClose={handleCloseModal}
					>
						{getAction()}
					</ModalWrapper>
				}
				<RoundedAddButton onClick={() => setOpenModal(true)} />
			</div>

			{
				openModal &&
				<Modal
					open={openModal}
					onClose={() => setOpenModal(false)}
					aria-labelledby="form-dialog-title"
				>
					<InventoryAddModal
						subcontractorsProducts={subcontractorsProducts}
						handleClose={() => setOpenModal(false)}
						handleUpdateItem={handleUpdatePreexistingInventoryItem}
						hub={selectedHub[0].objectId}
						refresh={refresh}
					/>
				</Modal>
			}

			{/* Error snackbar */}
			<ReceptionSnackBar data={inventorySnackBar} onClose={closeInventoryDetailSnackBar} />

			{/* apply styles adjustments */}
			<GlobalStyles />

		</FullScreenWrapper>
	)
}

export default connect((state) => {
	return {
		selectedHub: state.inventory.hub,
		formattedInventoryData: state.inventory.inventoryData,
		inventorySnackBar: state.inventory.inventoryDetailSnackBar,
		update: state.inventory.update,
		originalInventoryData: state.inventory.originalInventoryData,
		canEdit: state.app.rights[CAN_UPDATE_PRODUCT_STOCK],
		noDisplay: state.inventory.noDisplay,
		fridgeFilter: state.inventory.fridgeFilter
	}
}, {
	closeInventoryDetailSnackBar,
	showInventoryInterface,
	updateProductStock,
	updateProductStockInventoryByFridge,
	transferProductStock,
	updateStatus,
	resetStatus,
	filterByStatus,
	filterByNameOrCode,
	showErrors,
	loadInventory,
	sendProductPlacement,
	filterByFridge
})(Inventory)